<script lang="ts">
	import { Device, DeviceGroup } from "luxedo-data"
	import { onMount } from "svelte"
	import GroupNetworkRequirementEntry from "./GroupNetworkRequirementEntry.svelte"
	import { APIThread } from "luxedo-data/src/modules/device-operation-managers/DeviceAPIThread"
	import { CollapsibleSectionNoBind } from "misc-ui"
	import { ArrowDown } from "luxedo-icons"
	import GroupNetworkRequirementsMoreDetails from "./GroupNetworkRequirementsMoreDetails.svelte"
	import { GROUP_NETWORK_REQUIREMENT_LINK } from "../GroupConnectionTroubleshoot"

	type TestName = "myluxedo" | "amazonaws" | "support" | "timeserver"

	type Test = {
		result: boolean | undefined
		protocol: "TCP" | "UDP"
		ports: Array<number>
		destination: string
		purpose: string
	}

	const TEST_DEFAULTS = {
		myluxedo: {
			destination: "*.myluxedo.com",
			ports: [80, 443],
			protocol: "TCP" as const,
			purpose: "Firmware updates & essential functionality",
			result: undefined,
		},
		amazonaws: {
			destination: "*.amazonaws.com",
			ports: [443],
			protocol: "TCP" as const,
			purpose: "Luxedo Media Server / CDN",
			result: undefined,
		},
		support: {
			destination: "*.myluxedo.com",
			ports: [22, 2222, 30022],
			protocol: "TCP" as const,
			purpose: "Diagnostic ports for Luxedo engineers",
			result: undefined,
		},
		timeserver: {
			destination: "pool.ntp.org",
			ports: [123],
			protocol: "UDP" as const,
			purpose: "Time servers (important)",
			result: undefined,
		},
	}

	interface Props {
		group: DeviceGroup
	}

	let { group }: Props = $props()

	let testResults: { [key in TestName]?: Test } = $state(TEST_DEFAULTS)

	async function beginTesting() {
		testResults = TEST_DEFAULTS

		const testDevice = group.getChildDevices().find((dev) => dev.isOnline) as unknown as Device
		const ignoreThreadId = testDevice.eidos?.api_threads?.check_network_requirements?.id
		await testDevice.platoCall("check_network_requirements", [])

		const res: { [key in TestName]: boolean } = await APIThread.waitFor(
			testDevice,
			"check_network_requirements",
			ignoreThreadId
		)

		for (const [id, result] of Object.entries(res)) {
			testResults[id as TestName].result = result
		}
	}

	onMount(beginTesting)
</script>

<div class="group-network-requirements">
	<p class="info">
		This test checks whether one device in the group can access required network services. For
		detailed requirements, see
		<a target="_blank" href={GROUP_NETWORK_REQUIREMENT_LINK}>this article</a>.
		<br />
		<span class="warn">
			⚠ If devices are on different networks, results may not apply to the full group. Ensure all
			devices are on the same network and re-run the test if needed.
		</span>
	</p>
	<table>
		<thead>
			<tr>
				<th>Status</th>
				<th>Protocol</th>
				<th>Ports</th>
				<th>Destination</th>
				<th>Purpose</th>
			</tr>
		</thead>
		<tbody>
			{#each Object.entries(testResults) as [id, data] (id)}
				<GroupNetworkRequirementEntry {...data} />
			{/each}
		</tbody>
	</table>
</div>

<style>
	.group-network-requirements {
		overflow: auto;
		flex-grow: 1;
	}

	.info {
		color: var(--color-text);
		line-height: 1.25em;
	}

	.warn {
		font-weight: bold;
	}

	a {
		border: 0;
		color: var(--color-main);
	}

	table {
		width: 100%;
	}

	:global(.isMobile) .group-network-requirements {
		margin-top: 2rem;
	}

	table th,
	table :global(td) {
		padding: 0.25rem;
		line-height: 1.25em;

		text-align: center;
		vertical-align: middle;
		height: 100%;
	}

	th:first-of-type,
	table :global(td):first-of-type {
		padding-left: 0;
	}

	:global(.isMobile) table th,
	:global(.isMobile) table :global(td) {
		font-size: var(--h3);
	}

	th {
		font-weight: bold;
	}
</style>
